import React, { useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch, message, Tag, Empty, Alert,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useListMerchantsQuery, useCreateMerchantMutation, useUpdateMerchantMutation } from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

export default function MerchantsPage() {
  const { data, isLoading, isError, error } = useListMerchantsQuery();
  const [createMerchant] = useCreateMerchantMutation();
  const [updateMerchant] = useUpdateMerchantMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleOpenCreate = () => {
    setEditingMerchant(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalOpen(true);
  };

  const handleOpenEdit = (merchant) => {
    setEditingMerchant(merchant);
    form.setFieldsValue({
      ...merchant,
      is_active: !!merchant.is_active,
    });
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editingMerchant) {
        await updateMerchant({ merchantId: editingMerchant.id, ...values }).unwrap();
        message.success('Merchant updated');
      } else {
        await createMerchant(values).unwrap();
        message.success('Merchant created');
      }
      setModalOpen(false);
    } catch (err) {
      message.error(err.data?.title || 'Failed to save merchant');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Domain', dataIndex: 'domain', key: 'domain' },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button size="small" onClick={() => handleOpenEdit(record)}>Edit</Button>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Merchants"
        subtitle="Manage storefronts used for deal attribution"
        extra={(
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Add Merchant
          </Button>
        )}
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load merchants'}
        />
      )}

      <div style={adminPanelStyle}>
        <Table
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          size="middle"
          locale={{ emptyText: <Empty description="No merchants yet" /> }}
        />
      </div>

      <Modal
        title={editingMerchant ? 'Edit Merchant' : 'Add Merchant'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="domain" label="Domain">
            <Input placeholder="bestbuy.com" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="logo_url" label="Logo URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
